'use client'

import { useState } from 'react'
import { Smartphone, Monitor } from 'lucide-react'
import type { ProcessedTheme, BrowserRow } from './processedTypes'
import { watchClasses, compactDate, findDescription } from './processedTypes'

const DESCRIPTION_LABELS: Record<string, string> = {
  screenshot_mobile_description: 'Mobile',
  screenshot_desktop_description: 'Desktop',
}
import { CopyChip, ExpandToggle } from './sharedComponents'
import { Widget } from '../../Widget'

function browserDataOnCount(row: BrowserRow) {
  return [row.mobileIsAlive, row.desktopIsAlive].filter((alive) => alive === true).length
}

function BrowserDataSnapshotCard({
  title,
  row,
  metric,
  theme,
  showJson,
  onToggleJson,
}: {
  title: string
  row: BrowserRow
  metric?: string
  theme: ProcessedTheme
  showJson?: boolean
  onToggleJson?: () => void
}) {
  const c = watchClasses(theme)
  const status = (alive: boolean | null) => alive === true ? 'ALIVE' : alive === false ? 'DOWN' : 'UNKNOWN'
  const dot    = (alive: boolean | null) => alive === true ? 'bg-success' : alive === false ? 'bg-destructive' : 'bg-subtle'
  return (
    <div className={`rounded-md border p-4 ${c.line} ${c.soft}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className={`font-mono text-[10px] uppercase tracking-[0.24em] ${c.label}`}>{title}</div>
          <div className={`mt-2 font-mono text-sm ${c.text}`}>{compactDate(row.createdAt)}</div>
        </div>
        <div className="flex items-center gap-2">
          {metric && (
            <span className="rounded border border-success/40 bg-success-surface px-2 py-1 font-mono text-xs text-success">
              {metric}
            </span>
          )}
          {row.rawBrowserMetadata && onToggleJson && (
            <button
              onClick={onToggleJson}
              className="font-mono text-[10px] uppercase tracking-widest text-signal hover:text-signal"
            >
              {showJson ? 'Hide' : 'JSON'}
            </button>
          )}
        </div>
      </div>
      <div className={`mt-3 flex flex-wrap gap-1.5 font-mono text-xs ${c.muted}`}>
        <span className={`inline-flex items-center gap-1 rounded border px-2 py-1 ${c.line} bg-card`}>
          <Smartphone className="h-3 w-3" />
          <span className={`h-2 w-2 rounded-full ${dot(row.mobileIsAlive)}`} />
          MOBILE {status(row.mobileIsAlive)}
        </span>
        <span className={`inline-flex items-center gap-1 rounded border px-2 py-1 ${c.line} bg-card`}>
          <Monitor className="h-3 w-3" />
          <span className={`h-2 w-2 rounded-full ${dot(row.desktopIsAlive)}`} />
          DESKTOP {status(row.desktopIsAlive)}
        </span>
      </div>
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs">
        <span><span className={`mr-1 font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>Row</span><CopyChip value={row.id} /></span>
        {row.r2Key && <span><span className={`mr-1 font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>R2</span><CopyChip value={row.r2Key} display={row.r2Key.slice(0, 22) + '…'} /></span>}
      </div>
      {row.descriptions && row.descriptions.filter((d) => d.description).length > 0 && (
        <div className="mt-3 space-y-1.5">
          {row.descriptions.filter((d) => d.description).map((d) => (
            <div key={d.id} className={`rounded border px-2.5 py-1.5 text-xs leading-relaxed ${c.line} bg-card`}>
              <span className={`mr-1.5 font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>
                {DESCRIPTION_LABELS[d.tag] ?? d.tag}
              </span>
              <span className={c.muted}>{d.description}</span>
            </div>
          ))}
        </div>
      )}
      {showJson && row.rawBrowserMetadata && (
        <pre className={`mt-3 max-h-52 overflow-y-auto rounded-md border p-3 text-[10px] leading-relaxed border-border bg-secondary text-muted-foreground`}>
          {JSON.stringify(row.rawBrowserMetadata, null, 2)}
        </pre>
      )}
    </div>
  )
}

export function BrowserDataPanel({
  curBrowserData,
  theme,
}: {
  curBrowserData: BrowserRow[]
  latestBrowser: BrowserRow | null  // kept for API compatibility
  theme: ProcessedTheme
}) {
  const [showBetween, setShowBetween] = useState(false)
  const [showOlder,   setShowOlder]   = useState(false)
  const [openJsonId,  setOpenJsonId]  = useState<string | null>(null)
  const c = watchClasses(theme)

  const sorted = [...curBrowserData].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  )

  const last = sorted[0] ?? null
  const lastSuccessful = sorted.find(
    (r) => r.mobileIsAlive === true || r.desktopIsAlive === true,
  ) ?? null

  const lastSuccessfulTime = lastSuccessful ? new Date(lastSuccessful.createdAt).getTime() : -Infinity
  const between: BrowserRow[] = []
  const older:   BrowserRow[] = []

  for (const row of sorted) {
    if (row.id === last?.id || row.id === lastSuccessful?.id) continue
    if (lastSuccessful && new Date(row.createdAt).getTime() > lastSuccessfulTime) {
      between.push(row)
    } else {
      older.push(row)
    }
  }

  const toggleJson = (id: string) => setOpenJsonId((prev) => (prev === id ? null : id))

  return (
    <Widget label={<>Browser data · {curBrowserData.length} run{curBrowserData.length !== 1 ? 's' : ''}</>} theme={theme}>

      {last ? (
        <div className="space-y-3">
          {/* Always visible: most recent run */}
          <BrowserDataSnapshotCard
            title="Last run"
            row={last}
            metric={`${browserDataOnCount(last)}/2 on`}
            theme={theme}
            showJson={openJsonId === last.id}
            onToggleJson={() => toggleJson(last.id)}
          />

          {/* Runs between last and lastSuccessful — collapsed by default */}
          {between.length > 0 && (
            <>
              <ExpandToggle count={between.length} open={showBetween} onToggle={() => setShowBetween((v) => !v)} label="between" c={c} />
              {showBetween && between.map((row) => (
                <BrowserDataSnapshotCard
                  key={row.id}
                  title={compactDate(row.createdAt)}
                  row={row}
                  metric={`${browserDataOnCount(row)}/2 on`}
                  theme={theme}
                  showJson={openJsonId === row.id}
                  onToggleJson={() => toggleJson(row.id)}
                />
              ))}
            </>
          )}

          {/* Always visible: most recent successful run (if different from last) */}
          {lastSuccessful && lastSuccessful.id !== last.id && (
            <BrowserDataSnapshotCard
              title="Last successful"
              row={lastSuccessful}
              metric={`${browserDataOnCount(lastSuccessful)}/2 on`}
              theme={theme}
              showJson={openJsonId === lastSuccessful.id}
              onToggleJson={() => toggleJson(lastSuccessful.id)}
            />
          )}

          {/* Runs older than lastSuccessful — collapsed by default */}
          {older.length > 0 && (
            <>
              <ExpandToggle count={older.length} open={showOlder} onToggle={() => setShowOlder((v) => !v)} label="older" c={c} />
              {showOlder && older.map((row) => (
                <BrowserDataSnapshotCard
                  key={row.id}
                  title={compactDate(row.createdAt)}
                  row={row}
                  metric={`${browserDataOnCount(row)}/2 on`}
                  theme={theme}
                  showJson={openJsonId === row.id}
                  onToggleJson={() => toggleJson(row.id)}
                />
              ))}
            </>
          )}
        </div>
      ) : (
        <span className={`font-mono text-xs uppercase tracking-[0.28em] ${c.faint}`}>No browser data</span>
      )}
    </Widget>
  )
}
